import { z } from "zod";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Input, Modal } from "antd";
import { useNavigate } from "react-router-dom";

import { ErrorLabel } from "../../Components/atomic";
import { useEditFolder, useRenameS3Folder } from "../../api/fileLibrary";
import { ControlledField, useRHForm } from "../../Components/form";

const EditFolder = ({ name, isOpen, onClose, id, refetch, mode = "db", prefix = "" }: any) => {
  const dbMutation = useEditFolder(id);
  const s3Mutation = useRenameS3Folder();
  const navigate = useNavigate();

  const isS3 = mode === "s3";
  const { mutateAsync, isLoading, error } = isS3 ? s3Mutation : dbMutation;

  const [isRenaming, setIsRenaming] = useState(false);

  const {
    Form,
    methods: {
      control,
      setValue,
      reset,
      formState: { errors },
    },
  } = useRHForm({
    initialValues: {},
    schema: z.object({
      name: z.string(),
    }),
  });

  useEffect(() => {
    if (name) {
      setValue("name", name);
    }
  }, [name, setValue]);

  const handleSubmit = (data: any) => {
    if (isS3) {
      // Backend now accepts and returns relative prefixes
      const oldPrefix = (prefix || "").replace(/\\/g, "/").replace(/^\/+/, "").replace(/\/*$/, "/");

      setIsRenaming(true);
      mutateAsync({ old_prefix: oldPrefix, new_name: data.name })
        .then((res) => {
          if (res?.data?.data) {
            const newPrefix = res.data.data.new_prefix;
            reset();
            onClose();
            toast.success(
              `Folder renamed successfully (${res.data.data.files_moved} file${res.data.data.files_moved !== 1 ? "s" : ""} moved).`
            );
            // Navigate to the new prefix directly (it is returned as relative)
            const relativeNewPrefix = newPrefix;
            navigate(
              `/web-twinprocms/file-library?prefix=${encodeURIComponent(relativeNewPrefix)}`,
              { replace: true }
            );
          }
        })
        .catch((err) => {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Failed to rename folder.";
          toast.error(msg);
        })
        .finally(() => {
          setIsRenaming(false);
        });
    } else {
      // DB-backed rename (unchanged)
      mutateAsync(data)
        .then((res) => {
          if (res?.data) {
            reset();
            onClose();
            toast.success("Folder updated successfully.");
            refetch();
          }
        })
        .catch((error) => {
          toast.error(error?.message);
        });
    }
  };

  const activeLoading = isS3 ? isRenaming : isLoading;

  return (
    <Modal
      open={isOpen}
      title="Update SubFolder"
      onOk={onClose}
      onCancel={isRenaming ? undefined : onClose}
      closable={!isRenaming}
      maskClosable={!isRenaming}
      footer={[]}
    >
      <Form onSubmit={handleSubmit}>
        <ControlledField
          required
          name="name"
          label="Name"
          control={control}
          Component={Input}
          errors={errors}
        />
        <ErrorLabel>
          {isS3
            ? (error?.response?.data as any)?.error?.toString()
            : (error?.response?.data as any)?.name?.toString()}
        </ErrorLabel>
        {isS3 && isRenaming && (
          <p className="text-sm text-yellow-400 mt-2">
            Renaming folder… this may take a moment for folders with many files.
          </p>
        )}
        <div className="flex justify-end pt-4 ">
          <button
            className="bg-[#3d4e65] py-2 px-4 text-white rounded mr-4"
            onClick={onClose}
            disabled={isRenaming}
            type="button"
          >
            Close
          </button>
          ,
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            loading={activeLoading}
            disabled={activeLoading}
            className="bg-[#299aff] py-2 px-4 text-white rounded"
          >
            {isRenaming ? "Renaming…" : "Update"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditFolder;
